import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Fab, Typography, CircularProgress, Button } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import * as tf from '@tensorflow/tfjs';
import * as cocoSsd from '@tensorflow-models/coco-ssd';

const CameraScreen = ({ cameraStream, onCapture, tipoVehiculo }) => {
  const videoRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [hasCaptured, setHasCaptured] = useState(false);
  const [detectedVehicle, setDetectedVehicle] = useState(null);
  const [error, setError] = useState(null);
  const rafRef = useRef(null);
  const modelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('CameraStream recibido en CameraScreen:', cameraStream);
    let isMounted = true;
    const video = videoRef.current;

    const setupCamera = async () => {
      if (!video || !cameraStream) {
        setError('No se recibió el stream de la cámara.');
        return;
      }

      const tracks = cameraStream.getVideoTracks();
      console.log('Tracks del stream:', tracks);
      if (tracks.length === 0) {
        setError('El stream no contiene tracks de video.');
        return;
      }

      tracks.forEach(track => {
        console.log(`Track ${track.label}: enabled=${track.enabled}, readyState=${track.readyState}`);
        if (!track.enabled || track.readyState !== 'live') {
          setError(`El track de video (${track.label}) no está activo.`);
        }
      });

      video.srcObject = cameraStream;
      try {
        await video.play();
        console.log('Video reproduciéndose correctamente');
        if (isMounted) detectInRealTime(video);
      } catch (err) {
        setError(`Error al reproducir el video: ${err.message}`);
        console.error('Error al reproducir video:', err);
      }
    };

    setupCamera();

    return () => {
      isMounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (video) video.pause();
      if (modelRef.current) {
        modelRef.current.dispose();
        tf.disposeVariables();
      }
    };
  }, [cameraStream]);

  const detectInRealTime = async (video) => {
    try {
      if (!modelRef.current) {
        modelRef.current = await cocoSsd.load({ base: 'lite_mobilenet_v2' });
      }
  
      let lastDetectionTime = 0;
      const detectionInterval = 1000;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const angles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

      const detectFrame = async (timestamp) => {
        if (!videoRef.current) return;
  
        if (timestamp - lastDetectionTime >= detectionInterval) {
          tf.engine().startScope();
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
  
          let detectedVehicle = null;
          for (const angle of angles) {
            ctx.clearRect(0, 0, canvas.width, canvas.height); // Limpiar canvas
            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(angle);
            ctx.drawImage(video, -canvas.width / 2, -canvas.height / 2);
            ctx.restore();
  
            const predictions = await modelRef.current.detect(canvas, 1, 0.5); // Solo 1 predicción, umbral más alto
            const vehicle = predictions.find(pred => 
              ['car', 'motorcycle', 'truck'].includes(pred.class)
            );
  
            if (vehicle) {
              const [x, y, width, height] = vehicle.bbox;
              let adjustedX = x;
              let adjustedY = y;
              if (angle === Math.PI / 2) {
                adjustedX = y;
                adjustedY = canvas.width - (x + width);
              } else if (angle === Math.PI) {
                adjustedX = canvas.width - (x + width);
                adjustedY = canvas.height - (y + height);
              } else if (angle === 3 * Math.PI / 2) {
                adjustedX = canvas.height - (y + height);
                adjustedY = x;
              }
              vehicle.bbox = [adjustedX, adjustedY, width, height];
              detectedVehicle = { type: vehicle.class, bbox: vehicle.bbox };
              break;
            }
          }
  
          setDetectedVehicle(detectedVehicle);
          lastDetectionTime = timestamp;
          tf.engine().endScope();
        }
  
        rafRef.current = requestAnimationFrame(detectFrame);
      };
  
      rafRef.current = requestAnimationFrame(detectFrame);
    } catch (error) {
      console.error('Error en detección:', error);
      setError('Error en la detección de objetos.');
    }
  };

  const isPhotoWellTaken = (vehicle, videoWidth, videoHeight) => {
    if (!vehicle) return { valid: false, message: 'No se detectó un vehículo.' };

    const [x, y, width, height] = vehicle.bbox;
    const frameWidth = videoWidth * 0.9;
    const frameHeight = videoHeight * 0.8;
    const minSize = Math.min(frameWidth, frameHeight) * 0.5;

    const isLargeEnough = width > minSize && height > minSize;
    const isCentered = x > videoWidth * 0.05 && 
                      y > videoHeight * 0.1 && 
                      (x + width) < videoWidth * 0.95 && 
                      (y + height) < videoHeight * 0.9;

    if (!isLargeEnough) return { valid: false, message: 'El vehículo es demasiado pequeño o está cortado.' };
    if (!isCentered) return { valid: false, message: 'El vehículo no está dentro del marco.' };
    return { valid: true, message: '¡Foto lista para capturar!' };
  };

  const takePicture = async () => {
    const video = videoRef.current;
    if (!video) return;

    const expectedTypes = tipoVehiculo === 'Auto' ? ['car', 'truck'] : ['motorcycle'];
    if (!detectedVehicle || !expectedTypes.includes(detectedVehicle.type)) {
      alert(detectedVehicle 
        ? `Se esperaba un ${tipoVehiculo === 'Auto' ? 'auto o camión' : 'motocicleta'} pero se detectó un ${detectedVehicle.type}.`
        : 'No se detectó ningún vehículo.');
      return;
    }

    const photoValidation = isPhotoWellTaken(detectedVehicle, video.videoWidth, video.videoHeight);
    if (!photoValidation.valid) {
      alert(photoValidation.message);
      return;
    }

    setIsCapturing(true);
    setHasCaptured(true);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const vehicleInfo = { tipo: detectedVehicle.type };
      onCapture(blob, vehicleInfo);
      setIsCapturing(false);
    }, 'image/jpeg', 0.9);
  };

  const handleRetry = () => {
    setError(null);
    navigate('/'); // Volver a CameraPermissionScreen
  };

  const borderColor = detectedVehicle ? 'green' : 'red';

  return (
    <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted // Agregar muted para evitar problemas de audio
        style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'black' }}
      />
      <Box sx={{ position: 'absolute', top: 20, width: '100%', textAlign: 'center' }}>
        {error ? (
          <>
            <Typography variant="h6" sx={{ color: 'red' }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={handleRetry}
              sx={{ mt: 2, bgcolor: 'black', color: 'white', '&:hover': { bgcolor: '#333' } }}
            >
              Reintentar
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ color: borderColor }}>
              {detectedVehicle ? `Detectado: ${detectedVehicle.type}` : 'No se detecta vehículo'}
            </Typography>
            <Typography variant="body1" sx={{ color: borderColor }}>
              Asegúrate de capturar todo el vehículo dentro del marco.
            </Typography>
          </>
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '90%',
          height: '80%',
          border: `3px solid ${borderColor}`,
          borderRadius: 10,
        }}
      />
      {isCapturing ? (
        <CircularProgress
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'green',
          }}
        />
      ) : (
        <Fab
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: hasCaptured ? 'grey' : 'black',
            color: 'white',
            '&:hover': { bgcolor: hasCaptured ? 'grey' : '#333' },
          }}
          onClick={takePicture}
          disabled={hasCaptured || error}
        >
          <CameraAltIcon />
        </Fab>
      )}
    </Box>
  );
};

export default CameraScreen;